import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  AlertCircle, 
  Clock, 
  MapPin, 
  ArrowDown, 
  Globe, 
  Sun, 
  Sunset, 
  Moon, 
  FileText,
  Save,
  X,
  Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Database } from '../../types/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import { format, parseISO, isToday, isTomorrow, isYesterday, addDays } from 'date-fns';

type Itinerary = Database['public']['Tables']['itineraries']['Row'];
type Trip = Database['public']['Tables']['trips']['Row'];

export function Itineraries() {
  const { user } = useAuth();
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Itinerary | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    trip_id: '',
    day_number: 1,
    date: '',
    title: '',
    description: '',
    location: '',
    start_time: '',
    end_time: '',
    type: 'activity' as 'accommodation' | 'transportation' | 'activity' | 'meal' | 'free_time' | 'other',
    notes: ''
  });

  const activityTypeOptions = [
    { value: 'activity', label: 'Activity', icon: <MapPin size={16} /> },
    { value: 'transportation', label: 'Transportation', icon: <Clock size={16} /> },
    { value: 'accommodation', label: 'Accommodation', icon: <Calendar size={16} /> },
    { value: 'meal', label: 'Meal', icon: <FileText size={16} /> },
    { value: 'free_time', label: 'Free Time', icon: <Clock size={16} /> },
    { value: 'other', label: 'Other', icon: <FileText size={16} /> }
  ];

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);

        // Fetch trips
        const { data: tripsData, error: tripsError } = await supabase
          .from('trips')
          .select('*')
          .eq('user_id', user.id)
          .order('start_date', { ascending: false });

        if (tripsError) throw tripsError;
        setTrips(tripsData || []);

        // Fetch itineraries
        const { data: itinerariesData, error: itinerariesError } = await supabase
          .from('itineraries')
          .select(`
            *,
            trip:trips(title)
          `)
          .eq('user_id', user.id)
          .order('date', { ascending: true })
          .order('start_time', { ascending: true });

        if (itinerariesError) throw itinerariesError;
        setItineraries(itinerariesData || []);
      } catch (err) {
        console.error('Error loading itineraries:', err);
        setError(err instanceof Error ? err.message : 'Failed to load itineraries');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const handleAddItem = () => {
    setFormData({
      trip_id: '',
      day_number: 1,
      date: new Date().toISOString().split('T')[0],
      title: '',
      description: '',
      location: '',
      start_time: '',
      end_time: '',
      type: 'activity',
      notes: ''
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditItem = (item: Itinerary) => {
    setSelectedItem(item);
    setFormData({
      trip_id: item.trip_id || '',
      day_number: item.day_number,
      date: item.date,
      title: item.title,
      description: item.description || '',
      location: item.location || '',
      start_time: item.start_time || '',
      end_time: item.end_time || '',
      type: item.type || 'activity',
      notes: item.notes || ''
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewItem = (item: Itinerary) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  const handleDeleteItem = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this itinerary item?')) {
      try {
        const { error } = await supabase
          .from('itineraries')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        setItineraries(itineraries.filter(item => item.id !== id));
      } catch (err) {
        console.error('Error deleting item:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete item');
      }
    }
  };

  const getActivityTypeIcon = (type: string, size: number = 16) => {
    switch (type) {
      case 'flight':
        return <Plane size={size} className="text-blue-600" />;
      case 'transportation':
        return <Ticket size={size} className="text-indigo-600" />;
      case 'accommodation':
        return <Hotel size={size} className="text-amber-600" />;
      case 'activity':
        return <Globe size={size} className="text-purple-600" />;
      case 'meal':
        return <FileText size={size} className="text-red-600" />;
      default:
        return <Calendar size={size} className="text-gray-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);
    
    if (isToday(date)) {
      return 'Today';
    } else if (isTomorrow(date)) {
      return 'Tomorrow';
    } else if (isYesterday(date)) {
      return 'Yesterday';
    }
    
    return format(date, 'EEE, MMM d, yyyy');
  };

  // Filter itineraries based on search query and selected date
  const filteredItineraries = itineraries
    .filter(item => 
      (
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.notes?.toLowerCase().includes(searchQuery.toLowerCase())
      ) && 
      (selectedDate ? item.date === selectedDate : true)
    )
    .sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      const aTime = a.start_time || '00:00';
      const bTime = b.start_time || '00:00';
      return aTime.localeCompare(bTime);
    });

  // Get unique dates for the date filter
  const uniqueDates = [...new Set(itineraries.map(item => item.date))].sort();

  const handleSaveItem = async () => {
    if (!formData.title || !formData.date) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const itemData = {
        user_id: user!.id,
        trip_id: formData.trip_id || null,
        day_number: formData.day_number,
        date: formData.date,
        title: formData.title,
        description: formData.description || null,
        location: formData.location || null,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        type: formData.type,
        notes: formData.notes || null
      };

      if (isEditing && selectedItem) {
        const { data, error } = await supabase
          .from('itineraries')
          .update(itemData)
          .eq('id', selectedItem.id)
          .select()
          .single();
          
        if (error) throw error;
        setItineraries(itineraries.map(item => item.id === selectedItem.id ? data : item));
      } else {
        const { data, error } = await supabase
          .from('itineraries')
          .insert(itemData)
          .select()
          .single();
          
        if (error) throw error;
        setItineraries([data, ...itineraries]);
      }
      
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Itineraries</h1>
          <p className="text-gray-600 mt-1">
            Plan your daily travel activities and schedules
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddItem}
          icon={<Plus size={20} />}
        >
          Add Activity
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Search and Filter Options */}
      <div className="flex flex-col md:flex-row gap-4 md:items-center">
        <SearchInput 
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search itineraries..."
          className="flex-1"
        />
        <select
          value={selectedDate || ''}
          onChange={(e) => setSelectedDate(e.target.value || null)}
          className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Dates</option>
          {uniqueDates.map(date => (
            <option key={date} value={date}>
              {formatDate(date)}
            </option>
          ))}
        </select>
      </div>

      {/* Itinerary List */}
      <div className="space-y-8">
        {loading ? (
          <div className="animate-pulse space-y-8">
            {[1, 2].map(i => (
              <div key={i}>
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : filteredItineraries.length > 0 ? (
          (() => {
            // Group items by date
            const groupedByDate = filteredItineraries.reduce((acc, item) => {
              const date = item.date;
              if (!acc[date]) {
                acc[date] = [];
              }
              acc[date].push(item);
              return acc;
            }, {} as Record<string, Itinerary[]>);

            return Object.entries(groupedByDate).map(([date, items]) => (
              <Card key={date} className="overflow-hidden">
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-blue-600" />
                      <h2 className="text-xl font-semibold text-gray-900">
                        {formatDate(date)}
                      </h2>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddItem}
                      icon={<Plus size={16} />}
                    >
                      Add Item
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {items.map(item => (
                      <div 
                        key={item.id}
                        className="flex gap-3 p-3 border rounded-lg hover:bg-gray-50 group"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.start_time && (
                              <span className="text-sm text-gray-500 font-medium">
                                {item.start_time}
                                {item.end_time && ` - ${item.end_time}`}
                              </span>
                            )}
                            {getActivityTypeIcon(item.type || 'activity')}
                            <span className="font-medium text-gray-900">{item.title}</span>
                          </div>
                          {item.location && (
                            <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                              <MapPin size={14} />
                              <span>{item.location}</span>
                            </div>
                          )}
                          {item.description && (
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          )}
                          {item.notes && (
                            <p className="text-xs text-gray-500 mt-1 italic">{item.notes}</p>
                          )}
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ));
          })()
        ) : (
          <Card className="p-8 text-center">
            <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No itinerary found</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || selectedDate
                ? 'No itineraries match your search criteria.'
                : 'Start planning your trip activities by adding days to your itinerary.'}
            </p>
            {(!searchQuery && !selectedDate) && (
              <Button
                variant="primary"
                onClick={handleAddItem}
                icon={<Plus size={20} />}
              >
                Add Your First Activity
              </Button>
            )}
          </Card>
        )}
      </div>

      {/* Add Day Modal */}
      <Modal
        isOpen={showAddDayModal}
        onClose={() => setShowAddDayModal(false)}
        title="Add Itinerary Day"
        maxWidth="lg"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date*
            </label>
            <input
              type="date"
              value={dayFormData.date}
              onChange={(e) => setDayFormData({ ...dayFormData, date: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trip (Optional)
            </label>
            <select
              value={dayFormData.trip_id}
              onChange={(e) => setDayFormData({ ...dayFormData, trip_id: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={dayFormData.notes}
              onChange={(e) => setDayFormData({ ...dayFormData, notes: e.target.value })}
              rows={3}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this day"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddDayModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveDay}
            >
              Add Day
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Day Modal */}
      <Modal
        isOpen={showEditDayModal}
        onClose={() => {
          setShowEditDayModal(false);
          setSelectedDay(null);
        }}
        title="Edit Itinerary Day"
        maxWidth="lg"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date*
            </label>
            <input
              type="date"
              value={dayFormData.date}
              onChange={(e) => setDayFormData({ ...dayFormData, date: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trip (Optional)
            </label>
            <select
              value={dayFormData.trip_id}
              onChange={(e) => setDayFormData({ ...dayFormData, trip_id: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={dayFormData.notes}
              onChange={(e) => setDayFormData({ ...dayFormData, notes: e.target.value })}
              rows={3}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Add any notes about this day"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDayModal(false);
                setSelectedDay(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateDay}
            >
              Update Day
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Activity Modal */}
      <Modal
        isOpen={showAddActivityModal}
        onClose={() => {
          setShowAddActivityModal(false);
          setSelectedActivity(null);
          setSelectedDay(null);
        }}
        title={selectedActivity ? "Edit Activity" : "Add Activity"}
        maxWidth="xl"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time*
              </label>
              <input
                type="time"
                value={activityFormData.time}
                onChange={(e) => setActivityFormData({ ...activityFormData, time: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activity Type*
              </label>
              <select
                value={activityFormData.type}
                onChange={(e) => setActivityFormData({ ...activityFormData, type: e.target.value as any })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="flight">Flight</option>
                <option value="transportation">Transportation</option>
                <option value="accommodation">Accommodation</option>
                <option value="activity">Activity or Sightseeing</option>
                <option value="meal">Meal</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title*
            </label>
            <input
              type="text"
              value={activityFormData.title}
              onChange={(e) => setActivityFormData({ ...activityFormData, title: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Eiffel Tower Visit, Dinner at Le Petit Chef"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={activityFormData.location}
              onChange={(e) => setActivityFormData({ ...activityFormData, location: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Eiffel Tower, Paris"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={activityFormData.description}
              onChange={(e) => setActivityFormData({ ...activityFormData, description: e.target.value })}
              rows={3}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Add any details about this activity"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Booking Reference
            </label>
            <input
              type="text"
              value={activityFormData.booking_reference}
              onChange={(e) => setActivityFormData({ ...activityFormData, booking_reference: e.target.value })}
              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., ABC123"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddActivityModal(false);
                setSelectedActivity(null);
                setSelectedDay(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveActivity}
            >
              {selectedActivity ? 'Update Activity' : 'Add Activity'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Activity' : 'Add New Activity'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip (Optional)</label>
            <select
              value={formData.trip_id}
              onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day Number</label>
              <input
                type="number"
                min="1"
                value={formData.day_number}
                onChange={(e) => setFormData({ ...formData, day_number: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Activity title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
            <select
              value={formData.activity_type}
              onChange={(e) => setFormData({ ...formData, activity_type: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {activityTypeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Eiffel Tower, Paris"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
            <input
              type="number"
              step="0.01"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Activity description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            icon={<X size={16} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveItem}
            icon={saving ? undefined : <Save size={16} />}
            disabled={saving}
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Activity' : 'Create Activity')}
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedItem?.title || 'Activity Details'}
      >
        {selectedItem && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex items-center gap-1 text-blue-600">
                  {getActivityTypeIcon(selectedItem.activity_type)}
                  <span className="capitalize">{selectedItem.activity_type}</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                <p className="text-gray-900">Day {selectedItem.day_number}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <p className="text-gray-900">{formatDate(selectedItem.date)}</p>
            </div>

            {selectedItem.location && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <p className="text-gray-900">{selectedItem.location}</p>
              </div>
            )}

            {(selectedItem.start_time || selectedItem.end_time) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <p className="text-gray-900">
                  {selectedItem.start_time && formatTime(selectedItem.start_time)}
                  {selectedItem.start_time && selectedItem.end_time && ' - '}
                  {selectedItem.end_time && formatTime(selectedItem.end_time)}
                </p>
              </div>
            )}

            {selectedItem.cost && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                <p className="text-gray-900">{selectedItem.cost} {selectedItem.currency}</p>
              </div>
            )}

            {selectedItem.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-gray-900">{selectedItem.description}</p>
              </div>
            )}

            {selectedItem.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-gray-900">{selectedItem.notes}</p>
              </div>
            )}

            {selectedItem.trip_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                <p className="text-gray-900">{trips.find(t => t.id === selectedItem.trip_id)?.title || 'Unknown Trip'}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
          {selectedItem && (
            <Button
              variant="primary"
              onClick={() => {
                setShowViewModal(false);
                handleEditItem(selectedItem);
              }}
              icon={<Edit size={16} />}
            >
              Edit Activity
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
}